import { useQueryState } from "nuqs";
import { useEffect, useMemo, useState } from "react";

import { NotificationType } from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";

import { ProductDropDownList } from "@components/custom/dropdowns/product-dropdown-list";
import { SimpleSimPassButton } from "@components/sim-pass-button";
import { trackView } from "@lib/event-trackers";
import { useSimPass } from "@lib/hooks";
import { useNotificationContext } from "@contexts/notification-context";
import { hasAnyProducts } from "@lib/product.utils";
import { productParser } from "@lib/utils/search-params-parsers";
import { ViewUser } from "@models/tracking/events";
import container from "@src/container";

import { TraineeClassroomCeTab } from "./trainee.classroom.ce.tab";
import { TraineeClassroomProgTab } from "./trainee.classroom.prog.tab";
import { TraineeClassroomTrekTab } from "./trainee.classroom.trek.tab";
import { TraineeClassroomTsTab } from "./trainee.classroom.ts.tab";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

export function TraineeClassroomTab({
  trainee,
  classroom,
  organization,
  onRemoveTrainee,
  removeTraineeEnabled,
}) {
  const { classroomId, products, isArchived } = classroom;

  const [selectedProductId, setSelectedProductId] = useQueryState(
    "product",
    productParser(products[0])
  );

  const showSimPass =
    !trainee?.isAnonymous && selectedProductId !== Product.PROG && !isArchived;

  const canRemoveTrainee = removeTraineeEnabled && !isArchived;

  const isTsClassroom = useMemo(
    () => hasAnyProducts(products, [Product.TS]),
    [products]
  );

  const { addNotification } = useNotificationContext();

  const {
    deletingSimPass,
    generatingSimPass,
    simPass,
    generateSimPass,
    deleteSimPass,
  } = useSimPass({
    userId: trainee.userId,
    classroomId,
    product: isTsClassroom ? Product.TS : Product.CE,
    onError: addNotification,
  });


  useEffect(() => {
    const products = classroom.products;
    const isTSClass = products.includes(Product.TS);
    const isTrekWithCE = products.includes(Product.CE);

    if (isTSClass || isTrekWithCE) {
      trackView(
        ViewUser,
        `${getLearnerTerm()} Details`,
        classroom.name,
        String(isTSClass ? Product.TS : Product.CE),
        classroom.classroomId,
        trainee.userId
      );
    }
  }, [classroom]);


  const renderClassroomTab = (classroomProduct) => {
    switch (classroomProduct) {
      case Product.TS:
        return (
          <TraineeClassroomTsTab
            trainee={trainee}
            classroom={classroom}
            organization={organization}
            onRemoveTrainee={onRemoveTrainee}
            removeTraineeEnabled={canRemoveTrainee}
          />
        );
      case Product.CE:
        return (
          <TraineeClassroomCeTab
            trainee={trainee}
            classroom={classroom}
            onRemoveTrainee={onRemoveTrainee}
            removeTraineeEnabled={canRemoveTrainee}
          />
        );
      case Product.TRK:
        return (
          <TraineeClassroomTrekTab
            trainee={trainee}
            classroom={classroom}
            onRemoveTrainee={onRemoveTrainee}
            removeTraineeEnabled={canRemoveTrainee}
          />
        );
      case Product.PROG:
        return (
          <TraineeClassroomProgTab
            trainee={trainee}
            classroom={classroom}
            onRemoveTrainee={onRemoveTrainee}
            removeTraineeEnabled={canRemoveTrainee}
          />
        );
    }
  };

  const onProductSelected = ({ id }) => {
    setSelectedProductId(id);
  };

  const simPassProps = {
    deletingSimPass,
    generatingSimPass,
    simPass,
    user: trainee,
    onClick: generateSimPass,
    onDelete: deleteSimPass,
  };

  return (
    <>
      <div className="classroom-tab-header">
        {products.length > 1 && (
          <div className="product-select">
            <h2 className="product-select-label">Product</h2>
            <ProductDropDownList
              products={products}
              className="classroom-product-dropdown"
              onProductSelected={onProductSelected}
              defaultProduct={selectedProductId}
            />
          </div>
        )}
        {showSimPass && <SimpleSimPassButton {...simPassProps} />}
      </div>
      {renderClassroomTab(selectedProductId)}
    </>
  );
}
